import { MenuLink } from '@reach/menu-button';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { AppConfig } from '../../core-bundle/config/app-config';
import ProOnboardingCallout from '../ProOnboardingCallout/ProOnboardingCallout';
import { TransferAccountMenu } from '../TransferAccountMenu/TransferAccountMenu';
import Poptip from '@wetransfer/transfer/component/info/poptip';
import {
  NavigationSubmenu,
  NavigationSubmenuList,
  NavigationSubmenuButton,
} from '@wetransfer/transfer/component/NavigationSubmenu';
import PortalsCallout from '@wetransfer/transfer/component/PortalsCallout/PortalsCallout';
import { FF_TEAMS_TRANSFER_ONBOARDING } from '@wetransfer/transfer/constant/experiments';
import TeamDiscoveryExperiment from '@wetransfer/transfer/constant/experiments/team-discovery-experiment';
import { userHasPaidTier, getPlanTier } from '@wetransfer/transfer/helper/user';
import Analytics from '@wetransfer/transfer/modules/analytics';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import Actions from '@wetransfer/transfer/store/action';

import Config from './config';
import NavigationItem from './navigation-item';
import { isCurrentPath } from './navigation-item/helpers/navigation-item';

let NAV_LOADED = false;

const POPTIP_ANIMATION = {
  show: {
    properties: { opacity: [1, 0] },
    options: { duration: 300, easing: [0.55, 0.86, 0.69, 0.53] },
  },
  hide: {
    properties: { opacity: 0 },
    options: { duration: 300, easing: [0.55, 0.86, 0.69, 0.53] },
  },
};
const BASE_CLASS_NAME = 'nav';
const ITEM_BASE_CLASS_NAME = `${BASE_CLASS_NAME}__item`;

export class Navigation extends React.Component {
  constructor(props) {
    super(props);

    this.navigationRef = React.createRef();
    this.fullnavigationRef = React.createRef();
    this.portalsCalloutRef = React.createRef();

    this.doSignout = this.doSignout.bind(this);
  }

  componentDidMount() {
    // Because otherwise the fade-in animation happens
    // every time you open/close panel.
    this.fadeInTimeout = setTimeout(() => {
      NAV_LOADED = true;
    }, 300);

    this.startNotification();
  }

  componentWillUnmount() {
    clearTimeout(this.fadeInTimeout);
    clearTimeout(this.onboardingNotificationTimeout);
    clearTimeout(this.signinNotificationTimeout);
    clearTimeout(this.tooltipTimeout);
  }

  startNotification = () => {
    const { route, user, systemNotification } = this.props;
    const notifications = Config.getNotifications();

    const showOnboarding =
      route.query.claimedDomain &&
      !Cookie.get(`wt_${notifications.onboarding.name}`);

    const showSignin =
      user.loggedIn &&
      route.type === 'transfer' &&
      !Cookie.get(`wt_${notifications.signin.name}`);

    //return in case a notification is displayed next to the logo.
    if (systemNotification) {
      return;
    }

    if (showOnboarding) {
      this.onboardingNotificationTimeout = setTimeout(
        () => this.showNotification(notifications.onboarding.name),
        AppConfig.navigation.tooltip.showDelay
      );
    } else if (showSignin) {
      this.signinNotificationTimeout = setTimeout(
        () => this.showNotification(notifications.signin.name),
        AppConfig.navigation.tooltip.showDelay
      );
    }
  };

  showNotification = (details) => {
    this.props.setPopoverNotification('notification', details);
    // Set a cookie to prevent showing the tooltip again in the coming 24h
    Cookie.set(`wt_${details}`, 1, { days: 1 });
    // Hide tooltip after a certain delay
    this.tooltipTimeout = setTimeout(
      this.props.resetPopoverNotification,
      AppConfig.navigation.tooltip.hideDelay[details]
    );
  };

  getNotification(menuItem) {
    const { systemNotification, notification } = this.props;

    if (!systemNotification) {
      return false;
    }

    if (!Object.keys(notification.popover).length) {
      return false;
    }

    if (
      !menuItem.notificationDetails ||
      menuItem.notificationDetails.length === 0
    ) {
      return false;
    }

    if (!menuItem.notificationDetails.includes(notification.popover.details)) {
      return false;
    }

    const translationKey = `notification.${notification.popover.details}`;

    return I18n.t(translationKey) || notification.panel.details;
  }

  renderCallout() {
    let targetedItem;
    let renderPortalsCallout;

    if (
      Number(Cookie.get(AppConfig.cookies.teamsOnboardingCallout.name)) === 999
    ) {
      targetedItem =
        this.navigationRef.current?.children.namedItem('navigation.portals');
      renderPortalsCallout = true;
    } else {
      targetedItem = this.navigationRef.current?.children.namedItem(
        'navigation.branding'
      );
      renderPortalsCallout = false;
    }

    const coords = targetedItem?.getBoundingClientRect();
    const callout = this.portalsCalloutRef.current;

    const styles = {
      '--left': `${
        // We point the arrow on the callout to the center of the branding item and need to
        // account for the callout right margin (30px) and the bottom width of the arrow (15px)
        coords?.x + coords?.width / 2 - 15
      }px`,
    };

    //Array duplication that might be modified if navigation > callout width (see l.189)
    let appliedStyles = styles;

    //Comparison between navigation and callout size and calculate value of sizing variable
    const fullNavigationWidth = this.fullnavigationRef?.current.offsetWidth;
    const calloutWidth = callout?.offsetWidth;
    let calloutRightPosition;

    if (fullNavigationWidth > calloutWidth) {
      calloutRightPosition =
        window.innerWidth -
        this.fullnavigationRef?.current.getBoundingClientRect().left;

      const additionalStyles = {
        '--right-callout': `${calloutRightPosition}`,
      };
      appliedStyles = { ...styles, ...additionalStyles };
    }

    if (renderPortalsCallout) {
      return (
        <div ref={this.portalsCalloutRef} style={{ position: 'absolute' }}>
          <PortalsCallout calloutStyles={appliedStyles} />
        </div>
      );
    }

    if (getVariant(FF_TEAMS_TRANSFER_ONBOARDING) === 'on') {
      return <ProOnboardingCallout calloutStyles={appliedStyles} />;
    }
  }

  doSignout() {
    const { signout } = this.props;
    signout();
  }

  render() {
    const {
      route,
      layout,
      resetPopoverNotification,
      isLoading,
      user,
      pending,
      account,
      auth,
    } = this.props;
    const currentRouter = Router.currentRouter;

    let items = Config.getItems(isLoading);

    const className = classnames(BASE_CLASS_NAME, {
      [`${BASE_CLASS_NAME}--with-panel`]: route.type !== 'transfer',
      [`${BASE_CLASS_NAME}--loaded`]: NAV_LOADED,
      [`${BASE_CLASS_NAME}--html5-fullscreen`]: layout.hidden === true,
    });

    // Render a single back button item in the nav
    if (currentRouter) {
      const lastPathOptions = currentRouter.lastPathOptions;

      if (lastPathOptions.backTo) {
        items = Config.getBackButtonItem(
          lastPathOptions.backButtonText,
          lastPathOptions.backTo
        );
      }
    }

    const trackSubMenuParentClick = () => {
      Analytics.trackSnowplowEvent({
        category: 'navigation_bar',
        action: 'clickNavFeatures',
        label: getPlanTier(user) || 'anonymous',
      });
    };

    const trackSubMenuItemClick = (subItem) => {
      Analytics.trackSnowplowEvent({
        category: subItem.trackEvent.category,
        action: subItem.trackEvent.action,
        property: subItem.trackEvent.property,
        label: subItem.trackEvent.label,
      });
    };

    const navigationItems = items.map((item) => {
      const { path } = item;

      const subItems = (item.items || []).map((subItem) => {
        return (
          <MenuLink
            className="nav__link"
            role="menuitem"
            href={subItem.url}
            target="_blank"
            rel="noreferrer"
            aria-label="Opens in a new tab"
            key={I18n.t(subItem.name)}
            onSelect={() => {
              trackSubMenuItemClick(subItem);
            }}
          >
            {I18n.t(subItem.name)}
          </MenuLink>
        );
      });

      const isActive = isCurrentPath(route.path, path);

      const navigationItem = (
        <NavigationItem
          isActive={isActive}
          onClick={resetPopoverNotification}
          path={path}
          isLoading={isLoading && !user.loggedIn}
          {...item}
        />
      );

      const navigationItemClassName = classnames(ITEM_BASE_CLASS_NAME, {
        [`${ITEM_BASE_CLASS_NAME}--active`]: isActive,
      });

      const notification = this.getNotification(item);

      return item.items?.length > 0 ? (
        <li>
          <NavigationSubmenu key={item.name}>
            <NavigationSubmenuButton
              title={I18n.t(item.name)}
              onClick={trackSubMenuParentClick}
            />
            <NavigationSubmenuList>{subItems}</NavigationSubmenuList>
          </NavigationSubmenu>
        </li>
      ) : (
        <li key={item.name} id={item.name} className={navigationItemClassName}>
          {notification ? (
            <Poptip
              className="nav__item-tooltip"
              effects={POPTIP_ANIMATION}
              offset={12}
              autoHide={true}
              visible={true}
              trigger="manual"
              anchor="bottom-left"
              content={<span>{notification}</span>}
            >
              {navigationItem}
            </Poptip>
          ) : (
            navigationItem
          )}
        </li>
      );
    });

    // Pre-requisites to see any callouts
    // We need to check for the T&C so that the callout
    // does not appear over the cookie wall
    const shouldShowCallout =
      !route.isPanelOpen &&
      Cookie.get(AppConfig.cookies.termsAndConditions.name) &&
      userHasPaidTier(user) &&
      user.isVerified &&
      !TeamDiscoveryExperiment.isShowTeamDiscoveryExperiment() &&
      TeamDiscoveryExperiment.isInitialised();

    return (
      <nav
        className={className}
        ref={this.fullnavigationRef}
        aria-label={I18n.t('navigation.accessibility.aria_label')}
      >
        <ul className="nav__items" ref={this.navigationRef}>
          {navigationItems}
        </ul>
        {shouldShowCallout && this.renderCallout()}
        {user.loggedIn && !Browser.isMobile && !isLoading && (
          <TransferAccountMenu
            user={user}
            isLogOutPending={pending.signout}
            logout={this.doSignout}
            subdomainName={account.subdomain_name}
            auth0UserId={auth.auth0UserId}
          />
        )}
      </nav>
    );
  }
}

Navigation.propTypes = {
  account: PropTypes.object.isRequired,
  layout: PropTypes.object.isRequired,
  notification: PropTypes.object.isRequired,
  resetPopoverNotification: PropTypes.func.isRequired,
  route: PropTypes.object.isRequired,
  setPopoverNotification: PropTypes.func.isRequired,
  systemNotification: PropTypes.string,
  user: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  pending: PropTypes.object.isRequired,
  signout: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = ({
  account,
  layout,
  notification,
  route,
  user,
  pending,
  auth,
}) => ({
  account,
  layout,
  notification,
  route,
  user,
  pending,
  auth,
});

const mapDispatchToProps = {
  signout: Actions.User.signout.bind(Actions.User),
  ...Actions.Notification,
};

export default connect(mapStateToProps, mapDispatchToProps)(Navigation);
